'use client';

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type WidgetErrorBoundaryProps = {
  children: ReactNode;
  title?: string;
  className?: string;
};

type WidgetErrorBoundaryState = {
  error: Error | null;
};

function WidgetErrorFallback({
  title,
  className,
  error,
  onRetry,
}: {
  title?: string;
  className?: string;
  error: Error;
  onRetry: () => void;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-ds-border-subtlest bg-ds-surface-off-white p-6 text-center',
        className
      )}
    >
      <AlertTriangle className="size-5 text-ds-warning-default" />
      <p className="ds-text-body-sm-medium text-ds-text-default">
        {title ?? 'Something went wrong'}
      </p>
      {process.env.NODE_ENV === 'development' && (
        <p className="ds-text-body-xs-medium max-w-xs truncate text-ds-text-subtler">
          {error.message}
        </p>
      )}
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="mr-1.5 size-3.5" />
        Retry
      </Button>
    </div>
  );
}

/**
 * Component-level error boundary for dashboard widgets.
 *
 * Catches errors in children and renders a compact retry card
 * instead of crashing the entire page.
 *
 * @example
 * <WidgetErrorBoundary title="Chart failed to load">
 *   <RevenueChart />
 * </WidgetErrorBoundary>
 */
class WidgetErrorBoundary extends Component<
  WidgetErrorBoundaryProps,
  WidgetErrorBoundaryState
> {
  state: WidgetErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): WidgetErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[WidgetErrorBoundary]:', error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    const { children, title, className } = this.props;

    if (error) {
      return (
        <WidgetErrorFallback
          title={title}
          className={className}
          error={error}
          onRetry={this.handleRetry}
        />
      );
    }

    return children;
  }
}

export default WidgetErrorBoundary;
